import type { Options, SeriesLineOptions } from 'highcharts';
import { IntlShape } from 'react-intl';
import { exportMenuItems } from 'utils/highcharts';

import type { UnderlyingHistoric, UnderlyingHistorics } from './UnderlyingHistoModels';
import { GraphType } from './UnderlyingHistoModels';

export function getHistoData(data: UnderlyingHistoric): number[][] {
  return data.histo?.map(d => [d.date, d.close]);
}

export function optionsFromJson(
  historics: UnderlyingHistorics,
  graphType: GraphType,
  title: string,
  intl: IntlShape,
): Options {
  const { formatMessage } = intl;
  const histos: UnderlyingHistoric[] = historics.histos;
  const chartSeries: SeriesLineOptions[] = histos.map(histo => {
    return {
      name: histo.underlyingCode,
      data: getHistoData(histo),
      type: 'line',
      tooltip: {
        valueDecimals: graphType === GraphType.Multi ? 2 : undefined,
        pointFormatter() {
          const color = this.series.color;
          const name = this.series.name;
          const currency = histo.currency;
          const change = (this as any).change?.toFixed(2);
          const close = this.y?.toFixed(3);
          return `<span style="color:${color}">● </span><b>${name}</b>: <b>${close} ${currency}</b>${
            graphType === GraphType.Multi ? ` (${change}%)` : ''
          }`;
        },
      },
    };
  });

  const contextButtonTitle = formatMessage({ id: 'underlying.histograph.contextButtonTitle' });
  const printChart = formatMessage({ id: 'underlying.histograph.printChart' });
  const downloadPNG = formatMessage({ id: 'underlying.histograph.downloadPNG' });
  const downloadJPEG = formatMessage({ id: 'underlying.histograph.downloadJPEG' });
  const downloadSVG = formatMessage({ id: 'underlying.histograph.downloadSVG' });
  const downloadCSV = formatMessage({ id: 'underlying.histograph.downloadCSV' });
  const downloadXLS = formatMessage({ id: 'underlying.histograph.downloadXLS' });
  const viewFullscreen = formatMessage({ id: 'underlying.histograph.viewFullscreen' });
  const inputDateFormat = formatMessage({ id: 'underlying.histograph.inputDateFormat' });

  const d = formatMessage({ id: 'underlying.histograph.d', defaultMessage: 'd' });
  const m = formatMessage({ id: 'underlying.histograph.m', defaultMessage: 'm' });
  const y = formatMessage({ id: 'underlying.histograph.y', defaultMessage: 'y' });
  const ytd = formatMessage({ id: 'underlying.histograph.ytd', defaultMessage: 'YTD' });
  const all = formatMessage({ id: 'underlying.histograph.all', defaultMessage: 'All' });

  return {
    lang: {
      contextButtonTitle,
      printChart,
      downloadPNG,
      downloadJPEG,
      downloadSVG,
      downloadCSV,
      downloadXLS,
      viewFullscreen,
    },
    chart: {
      styledMode: true,
      zooming: { type: 'x' },
    },
    navigation: {
      buttonOptions: {
        enabled: true,
      },
    },
    exporting: {
      enabled: true,
      accessibility: {
        enabled: true,
      },
      allowHTML: false,
      allowTableSorting: true,
      buttons: {
        contextButton: {
          enabled: true,
          menuItems: [...exportMenuItems],
        },
      },
    },
    rangeSelector: {
      verticalAlign: 'bottom',
      inputBoxBorderColor: 'gray',
      inputBoxWidth: 120,
      inputBoxHeight: 18,
      inputStyle: {
        fontWeight: 'bold',
      },
      labelStyle: {
        color: 'silver',
        fontWeight: 'bold',
      },
      inputDateFormat,
      buttonTheme: {
        padding: 8,
      },
      buttonSpacing: 8,
      buttons: [
        {
          type: 'day',
          count: 5,
          text: `5${d}`,
        },
        {
          type: 'month',
          count: 1,
          text: `1${m})}`,
          title: 'View 1 month',
        },
        {
          type: 'month',
          count: 3,
          text: `3${m})}`,
          title: 'View 3 months',
        },
        {
          type: 'month',
          count: 6,
          text: `6${m}`,
          title: 'View 6 months',
        },
        {
          type: 'ytd',
          text: ytd,
          title: 'View year to date',
        },
        {
          type: 'year',
          count: 1,
          text: `1${y}`,
          title: 'View 1 year',
        },
        {
          type: 'all',
          text: all,
          title: 'View all',
        },
      ],
    },
    title: {
      text: title,
      style: {
        fontWeight: 'bold',
      },
    },
    legend: {
      enabled: true,
      align: 'right',
      verticalAlign: 'top',
      itemStyle: {
        fontWeight: 'bold',
      },
      itemHoverStyle: {
        fontWeight: 'bold',
      },
    },
    xAxis: {
      labels: {},
      type: 'datetime',
    },
    yAxis: {
      opposite: false,
      labels: {
        format: graphType === GraphType.Multi ? '{#if (gt value 0)}+{/if}{value}%' : '',
      },
      title: {
        text: `${formatMessage({ id: 'close' })} ${GraphType.Mono ? '' : ' %'}`,
      },
    },
    plotOptions: {
      series: {
        compare: graphType === GraphType.Multi ? 'percent' : undefined,
        showInNavigator: true,
      },
    },
    series: chartSeries,
    credits: {
      enabled: false,
    },
  };
}
